import { useState, useEffect, useCallback, useRef } from 'react';
import { api, GrRewardTypeLimitResponse } from '@/lib/api';

interface UseRewardTypeLimitsReturn {
  limits: GrRewardTypeLimitResponse[];
  total: number;
  loading: boolean;
  error: string | null;
  refresh: () => void;
}

export function useRewardTypeLimits(): UseRewardTypeLimitsReturn {
  const [limits, setLimits] = useState<GrRewardTypeLimitResponse[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const fetchIdRef = useRef(0);

  const fetchLimits = useCallback(async () => {
    const id = ++fetchIdRef.current;
    try {
      setLoading(true);
      const result = await api.listGameRewardsLimits();
      if (fetchIdRef.current !== id) return;
      setLimits(result.limits);
      setTotal(result.total);
    } catch (err) {
      if (fetchIdRef.current !== id) return;
      setError(err instanceof Error ? err.message : 'Failed to load reward type limits');
    } finally {
      if (fetchIdRef.current === id) {
        setLoading(false);
      }
    }
  }, []);

  const refresh = useCallback(() => {
    setError(null);
    fetchLimits();
  }, [fetchLimits]);

  useEffect(() => {
    refresh();
    return () => {
      fetchIdRef.current++;
    };
  }, [refresh]);

  return { limits, total, loading, error, refresh };
}
